/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

let _ = t => t,
    _t,
    _t2;

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import styled from 'styled-components';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { isMon569VariantSelector } from '../../selectors/ExperimentTreatmentSelectors';
import { hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { commonAvailableTermsSelector } from '../../selectors/OfferSelectors';
import { checkoutProductConfigurationsSelector, configsWithSuiteIfPossibleSelector } from '../../selectors/ProductConfigsSelectors';
import { hasProductVersionOrHigherProductLevelInProductInventorySelector, ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelSelector, skuIdIsHubSelector, skuIdIsPartOfCrmSuiteSelector } from '../../selectors/ProductsSelectors';
import { useSortedProductConfigurations } from '../../utils/customHooks';
import breakpoint from '../../utils/responsiveBreakpoints';
import { useQueryParamTerm } from '../../utils/stateUtils';
import { useProductConfigsQueryParamsParamsEffect, useQueryParamProductConfigsOrDefault } from './PlanBuilderQueryParamUtils';
export const PlanBuilderContext = /*#__PURE__*/React.createContext(null);
const Wrapper = styled.div.withConfig({
  displayName: "PlanBuilder__Wrapper",
  componentId: "sc-1uxvxvz-0"
})(["max-width:1024px;margin:0 auto;padding:0 15px;", ";"], breakpoint.phone(_t || (_t = _`
    padding: 0;
  `)));
const ResponsiveUIFlex = styled(UIFlex).withConfig({
  displayName: "PlanBuilder__ResponsiveUIFlex",
  componentId: "sc-1uxvxvz-1"
})(["", ";"], breakpoint.tablet(_t2 || (_t2 = _`
    flex-direction: column;
  `)));

const PlanBuilder = (_ref) => {
  let {
    initialProductConfigurations = [],
    children,
    gaTrackingId,
    trackingSource,
    screen,
    requiredProductLines = [],
    zeroStateCheckoutProductConfigs = [],
    allowUpgradesToCrmSuite = false,
    headerContent = null,
    showDiscountedPricesInUnitsPicker = false
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["initialProductConfigurations", "children", "gaTrackingId", "trackingSource", "screen", "requiredProductLines", "zeroStateCheckoutProductConfigs", "allowUpgradesToCrmSuite", "headerContent", "showDiscountedPricesInUnitsPicker"]);

  const initialProductConfigurationsUsingQueryParams = useQueryParamProductConfigsOrDefault({
    defaultConfigurations: initialProductConfigurations,
    requiredProductLines
  });
  const initialSuiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: initialProductConfigurationsUsingQueryParams.map(({
      skuId
    }) => skuId)
  }));
  const [suiteProductLevel, setSuiteProductLevel] = useState(initialSuiteProductLevel);
  const selectedOwnedSuiteProductLevel = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel: suiteProductLevel
  }));
  const filteredInitialProductConfigurations = useSelector(state => {
    return initialProductConfigurationsUsingQueryParams.filter(({
      skuId
    }) => {
      const allowedForCrmSuiteUpgrade = suiteProductLevel && skuIdIsPartOfCrmSuiteSelector(state, {
        skuId,
        suiteProductLevel
      });
      return allowedForCrmSuiteUpgrade || !(skuIdIsHubSelector(state, {
        skuId
      }) && hasProductVersionOrHigherProductLevelInProductInventorySelector(state, {
        skuId
      }));
    });
  });
  const [originalProductConfigurations, setProductConfigurations] = useSortedProductConfigurations(filteredInitialProductConfigurations); // On some pages we insert the rest of CRM suite if you own the remaining plans needed for it already

  const productConfigurations = useSelector(state => {
    if (allowUpgradesToCrmSuite) {
      return configsWithSuiteIfPossibleSelector(state, {
        productConfigurations: originalProductConfigurations
      });
    }

    return originalProductConfigurations;
  });
  const memoizedProductConfigurations = useMemo(() => productConfigurations, [originalProductConfigurations]);
  useProductConfigsQueryParamsParamsEffect(memoizedProductConfigurations);
  const checkoutProductConfigurations = useSelector(state => {
    const checkoutConfigs = checkoutProductConfigurationsSelector(state, {
      productConfigurations,
      subtractOwnedUnitsIfBuyingNoAdditional: true,
      subtractOwnedSuiteProducts: selectedOwnedSuiteProductLevel
    });
    if (checkoutConfigs.length === 0) return zeroStateCheckoutProductConfigs;
    return checkoutConfigs;
  });
  const {
    queryParamTerm,
    setQueryParamTerm
  } = useQueryParamTerm();
  const commonAllowedQueryParamTerm = useSelector(state => {
    const commonAvailableTerms = commonAvailableTermsSelector(state, {
      skuIds: checkoutProductConfigurations.map(({
        skuId
      }) => skuId)
    });
    return queryParamTerm === ANNUAL && commonAvailableTerms.length > 1 ? ANNUAL : MONTHLY;
  });
  return /*#__PURE__*/_jsx(PlanBuilderContext.Provider, {
    value: {
      productConfigurations: memoizedProductConfigurations,
      setProductConfigurations,
      queryParamTerm: queryParamTerm || commonAllowedQueryParamTerm,
      setQueryParamTerm: newTerm => setQueryParamTerm(newTerm),
      gaTrackingId,
      requiredProductLines,
      checkoutProductConfigurations,
      trackingSource,
      screen,
      showDiscountedPricesInUnitsPicker,
      setSuiteProductLevel
    },
    children: /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
      children: [headerContent, /*#__PURE__*/_jsx(ResponsiveUIFlex, {
        children: children
      })]
    }))
  });
};

const PlanBuilderLoadingWrapper = props => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isMon569Variant = useSelector(isMon569VariantSelector);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);

  if (hasPricingAndPurchasingSucceeded) {
    return /*#__PURE__*/_jsx(PlanBuilder, Object.assign({}, props));
  } else if (hasPricingAndPurchasingFailed) {
    return null;
  } else {
    const classNames = isMon569Variant ? 'flex-column justify-center align-start' : 'flex-column justify-center align-center';
    return /*#__PURE__*/_jsx("div", {
      style: {
        minHeight: 600,
        width: '100%'
      },
      className: classNames,
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {
        size: "medium"
      })
    });
  }
};

export default PlanBuilderLoadingWrapper;